import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { clamp } from 'three/src/math/MathUtils';
import { throttle } from 'underscore';

import { useDialog } from '../../../hooks/use-dialog';
import { Button } from '../../button/button';
import { CollapsableSection } from '../../skyview/right-menu/components/collapsable-section';
import { RightPanelFooter } from '../../skyview/right-menu/components/styles';
import { Stack } from '../../stack/stack';
import { GeodataImageList } from '../geodata-image-list/geodata-image-list';
import { GeodataImagesContext } from '../geodata-images-state';
import { GeodataLegendList } from '../geodata-legend-list/geodata-legend-list';
import { GeodataContext } from '../geodata-state';
import { AlignerContext } from './aligner-state';
import { BoundaryPolygonList } from './boundary-polygon-list';
import { GcpList } from './gcp-list';
import { MarkerList } from './markers-list';
import { PlaceMarkersContext } from './place-markers-state';
import { OptimizeDialog, StartProcessDialog } from './start-process-dialog';

const minWidth = 300;
const defaultWidth = 500;

export const SfmViewerRightMenu = ({ parentWidth }: { parentWidth: number }) => {
  const startProcessDialog = useDialog();
  const optimizeDialog = useDialog();
  const [imageListExpanded, setImageListExpanded] = useState(true);
  const [legendListExpanded, setLegendListExpanded] = useState(true);
  const [gcpListExpanded, setGcpListExpanded] = useState(true);
  const [markerListExpanded, setMarkerListExpanded] = useState(true);
  const [boundaryPolygonListExpanded, setBoundaryPolygonListExpanded] = useState(false);
  const { t } = useTranslation();
  const { setState, readyToQuickOptimize, isLoading } = useContext(AlignerContext);
  const { geodata } = useContext(GeodataContext);
  const { images, progress } = useContext(GeodataImagesContext);
  const { showUnoptimizedGcpWarning, showHighGcpErrorWarning, showLowGcpCountWarning, markers } =
    useContext(PlaceMarkersContext);
  const [panelWidth, setPanelWidth] = useState(defaultWidth);

  const imagesTitle = useMemo(() => {
    const text = t('imageList.title', { ns: 'cloudProcessing' });

    if (progress.state === 'pending') {
      return `${text} (${Math.floor(progress.value * images.length)} / ${images.length})`;
    }

    return images.length === 0 ? text : `${text} (${images.length})`;
  }, [images, t, progress]);

  const gcpListTitle = useMemo(() => {
    const text = t('gcpList.title', { ns: 'cloudProcessing' });
    const length = geodata.gcpCollection?.points.length ?? 0;
    return length === 0 ? text : `${text} (${length})`;
  }, [geodata, t]);

  const markersListTitle = useMemo(() => {
    const text = t('markerList.title', { ns: 'cloudProcessing' });
    return markers.length === 0 ? text : `${text} (${markers.length})`;
  }, [markers, t]);

  const onStartProcess = useCallback(() => {
    setState('process-pending');
  }, [setState]);

  const handleMouseDown = useCallback(
    (mouseDownEvent: React.MouseEvent, startWidth: number) => {
      mouseDownEvent.preventDefault();
      const maxWidth = Math.max(parentWidth / 2, defaultWidth);
      const onMouseMove = throttle((mouseMoveEvent: MouseEvent) => {
        const newWidth = startWidth + (mouseDownEvent.clientX - mouseMoveEvent.clientX);
        setPanelWidth(clamp(newWidth, minWidth, maxWidth));
        window.dispatchEvent(new Event('resize'));
      }, 50);

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [setPanelWidth, parentWidth],
  );

  return (
    <Container panelWidth={panelWidth}>
      <SfmRightPanelContent>
        <CollapsableSection
          expanded={boundaryPolygonListExpanded}
          title={t('boundaryPolygonList.title', { ns: 'cloudProcessing' })}
          onSetExpanded={(state) => setBoundaryPolygonListExpanded(state)}
        >
          <BoundaryPolygonList />
        </CollapsableSection>

        <CollapsableSection
          expanded={gcpListExpanded}
          title={gcpListTitle}
          onSetExpanded={(state) => setGcpListExpanded(state)}
        >
          <GcpList />
        </CollapsableSection>

        <CollapsableSection
          expanded={markerListExpanded}
          title={markersListTitle}
          onSetExpanded={(state) => setMarkerListExpanded(state)}
        >
          <MarkerList />
        </CollapsableSection>

        <CollapsableSection
          expanded={imageListExpanded}
          title={imagesTitle}
          onSetExpanded={(state) => setImageListExpanded(state)}
        >
          <GeodataImageList />
        </CollapsableSection>

        <CollapsableSection
          expanded={legendListExpanded}
          title={t('legendsList.title', { ns: 'cloudProcessing' })}
          onSetExpanded={(state) => setLegendListExpanded(state)}
        >
          <GeodataLegendList />
        </CollapsableSection>
      </SfmRightPanelContent>

      <RightPanelFooter>
        <Stack direction="row" spacing={1}>
          <Button
            color="secondary"
            loading={isLoading}
            variant="contained"
            width="100%"
            onClick={() => optimizeDialog.show()}
          >
            {t('optimize', { ns: 'cloudProcessing' })}
          </Button>
          <Button
            color="primary"
            loading={isLoading}
            variant="contained"
            width="100%"
            onClick={() => startProcessDialog.show()}
          >
            {t('startProcess', { ns: 'cloudProcessing' })}
          </Button>
        </Stack>
      </RightPanelFooter>

      <ResizeHandle onMouseDown={(e) => handleMouseDown(e, panelWidth)} />
      {optimizeDialog.render(
        <OptimizeDialog
          dialog={optimizeDialog}
          geodata={geodata}
          readyToQuickOptimize={readyToQuickOptimize}
          onStartProcess={onStartProcess}
        />,
      )}
      {startProcessDialog.render(
        <StartProcessDialog
          dialog={startProcessDialog}
          geodata={geodata}
          readyToQuickOptimize={readyToQuickOptimize}
          showHighGcpErrorWarning={showHighGcpErrorWarning}
          showImageThresholdExceededInfo={images.length > 2000}
          showLowGcpCountWarning={showLowGcpCountWarning}
          showUnoptimizedGcpWarning={showUnoptimizedGcpWarning}
          onStartProcess={onStartProcess}
        />,
      )}
    </Container>
  );
};

const SfmRightPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  z-index: 0;
`;

const Container = styled.div<{ panelWidth: number }>`
  width: ${(props) => props.panelWidth}px;
  flex: none;

  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
  position: relative;
`;

const ResizeHandle = styled.div`
  width: 10px;
  cursor: ew-resize;
  position: absolute;
  top: 0;
  bottom: 0;
  left: -5px;
  background-color: rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;
